import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import featureService from '../../../core/services/featureService';
import { getLogoPath, getLogoIconPath, BRAND_QUERY } from '../../utils/assetPaths';
import BrandProtectedImg from '../custom/BrandProtectedImg';
import { moduleStructure } from '../../../core/config/moduleStructure';
import { getModuleLabel, getSubsectionLabel } from '../../utils/moduleNavI18n';
import { useLayout } from '../../../core/contexts/LayoutContext';
import { useGlobalTheme } from '../../hooks/useGlobalTheme';
import { useSetup } from '../../../core/contexts/SetupContext';
import { secureGetItem } from '../../utils/secureStorage';
import SyncStatusIndicator from '../SyncStatusIndicator';
import ModuleBadge from '../ModuleBadge';
import AIAgentTriggerButton from '../../../ai-agent/components/AIAgentTriggerButton';
import {
  BellIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { 
  Receipt,
  FolderOpen,
  Archive,
  Users,
  Truck,
  Factory,
  Clock,
  DollarSign,
  Landmark,
  Vault,
  PanelLeft,
  LayoutDashboard,
  Package,
  Building2,
  UserCog,
  Boxes,
  ShoppingCart,
  Calculator,
  PieChart,
  Megaphone,
  ClipboardList,
  FileText,
  Wrench,
  Car,
  Store,
  Link2,
  Moon,
  Sun,
  Bell,
  ChevronDown,
  Settings,
  UserPlus,
  BookOpen,
  Calendar,
  Target,
  BarChart3,
  TrendingUp,
  RefreshCw,
  RotateCcw,
  AlertTriangle,
  FileSpreadsheet,
  Shield,
  MapPin,
  Route,
  Copy,
  Edit,
  Droplet,
  ShieldCheck,
  PenTool,
  Flag,
  GitBranch,
  CheckSquare,
  MessageSquare,
  Mail,
  Bell as BellLucide,
  Scale,
  ArrowRight,
  XCircle,
  ClipboardCheck,
  Wrench as WrenchIcon,
  Cog,
  Layers,
  Database,
  Wallet,
  CreditCard,
  ArrowDownToLine,
  ArrowUpFromLine,
  FilePlus,
  Banknote,
  FileCheck,
  AlertCircle,
  UserCheck,
  LineChart,
  Monitor,
  ChartBar,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { getModuleAlertesPath } from '../../utils/moduleAlertesRoute';
import './TopBar.css';

const TopBar = ({ isLightTheme = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const goToModuleAlertes = () => {
    navigate(getModuleAlertesPath(location.pathname));
  };
  const [user, setUser] = useState({ username: '', role: '' });
  const { logout } = useSetup();
  const { toggleLayout } = useLayout();
  const { isDashboardLightMode, toggleDashboardTheme } = useGlobalTheme();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRefs = useRef({});
  const modulesScrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  
  // Refs for click-outside detection
  const moduleButtonRefs = useRef({});
  const dropdownContainerRef = useRef(null);
  const leftArrowRef = useRef(null);
  const rightArrowRef = useRef(null);
  
  // 🎯 Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!activeDropdown) return;
      
      // Check if clicking inside dropdown
      if (dropdownContainerRef.current && dropdownContainerRef.current.contains(event.target)) {
        return; // Don't close if clicking inside dropdown
      }
      
      // Check if clicking on arrow buttons
      if ((leftArrowRef.current && leftArrowRef.current.contains(event.target)) ||
          (rightArrowRef.current && rightArrowRef.current.contains(event.target))) {
        return; // Don't close if clicking on scroll arrows
      }
      
      // Check if clicking on a module button
      const clickedButton = Object.values(moduleButtonRefs.current).find(btn => 
        btn && btn.contains(event.target)
      );
      
      // Close dropdown only if not clicking a module button
      if (!clickedButton) {
        setActiveDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  // Check scroll position to show/hide arrows
  const checkScroll = () => {
    if (modulesScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = modulesScrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Scroll modules left/right
  const scrollModules = (direction) => {
    if (modulesScrollRef.current) {
      const scrollAmount = 200;
      modulesScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Check scroll on mount and resize
  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  // Get user info from secure storage
  useEffect(() => {
    try {
      const storedUser = secureGetItem('qawaem_user');
      console.log('📦 TopBar loading user from secure storage:', storedUser);
      if (storedUser) {
        console.log('✅ TopBar setting user:', storedUser);
        setUser({
          username: storedUser.username || t('common.user'),
          role: storedUser.role || t('sidebar.user_role')
        });
      } else {
        console.warn('⚠️ No user data in TopBar');
        setUser({
          username: t('common.user'),
          role: t('sidebar.user_role')
        });
      }
    } catch (error) {
      console.error('❌ Error loading user from secure storage:', error);
    }
  }, [t]);

  // Filter modules by access
  const accessibleModules = moduleStructure.filter(module => 
    featureService.isFeatureEnabled(module.key)
  );

  return (
    <header className="bg-white/10 backdrop-blur-lg border-b border-white/20 sticky top-0 z-50 overflow-visible">
      {/* Main navigation */}
      <div className="flex flex-col overflow-visible">
        {/* Top section with Qawaem logo and user controls */}
        <div className="h-16 flex items-center justify-between px-4 md:px-6">
          {/* Qawaem Logo and app name */}
          <Link to="/dashboard" className="flex items-center">
            <BrandProtectedImg src={getLogoIconPath()} alt="Qawaem" className="h-9 w-9 mr-3" onError={(e) => { e.target.src = `${getLogoPath(isLightTheme)}${BRAND_QUERY}`; }} />
            <div>
              <h1 className={`text-lg font-semibold ${isLightTheme ? 'text-gray-900' : 'text-white'}`}>Qawaem</h1>
              <p className={`text-xs whitespace-nowrap ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`}>{t('common.app_tagline')}</p>
            </div>
          </Link>

          {/* Module Badge and Refresh button - Shows current module */}
          <div className="flex-1 flex justify-center items-center gap-2">
            {/* Global refresh button */}
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9, rotate: 180 }}
              onClick={() => {
                console.log('Refreshing all application data...');
                // Emit refresh events for all sections
                window.dispatchEvent(new CustomEvent('refresh-dashboard'));
                window.dispatchEvent(new CustomEvent('refresh-clients'));
                window.dispatchEvent(new CustomEvent('refresh-products'));
                window.dispatchEvent(new CustomEvent('refresh-categories'));
                window.dispatchEvent(new CustomEvent('refresh-employees'));
                window.dispatchEvent(new CustomEvent('refresh-documents'));
                window.dispatchEvent(new CustomEvent('refresh-settings'));
                window.dispatchEvent(new CustomEvent('refresh-all-data'));
              }}
              className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all duration-200"
              title={t('common.refresh')}
            >
              <RefreshCw className="h-5 w-5" />
            </motion.button>
            
            <ModuleBadge />
          </div>

          {/* User controls */}
          <div className="flex items-center space-x-2">
            {/* Sync Status */}
            <SyncStatusIndicator />

            {/* AI Agent */}
            <AIAgentTriggerButton />

            {/* Notifications — même comportement que Header (sidebar) */}
            <button
              type="button"
              onClick={goToModuleAlertes}
              className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 relative"
              title={t('common.notifications')}
              aria-label={t('common.notifications')}
            >
              <Bell className="h-5 w-5" />
            </button>

            {/* Theme Toggle - Clean Lucide icons */}
            <button
              onClick={toggleDashboardTheme}
              className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-all duration-200"
              title={isDashboardLightMode ? t('common.dark_mode') : t('common.light_mode')}
            >
              {isDashboardLightMode ? (
                <Moon className="h-5 w-5" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
            </button>

            {/* Layout Toggle */}
            <button
              onClick={toggleLayout}
              className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10"
              title={t('common.layout_switch_sidebar')}
            >
              <PanelLeft className="h-5 w-5" />
            </button>

            {/* User menu */}
            <div className="relative group">
              <button className="flex items-center text-white space-x-2 p-2 rounded-full hover:bg-white/10">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                  {user?.username?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              </button>

              <div className="absolute right-0 mt-2 w-48 bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="px-4 py-3 border-b border-white/10">
                  <p className="text-sm text-white">{user?.username || t('common.user')}</p>
                  <p className="text-xs text-white/60 truncate">{user?.role || t('sidebar.user_role')}</p>
                </div>
                <div className="py-1">
                  <Link
                    to="/settings"
                    className="text-white group flex w-full items-center px-4 py-2 text-sm transition-colors hover:bg-white/10"
                  >
                    <UserIcon className="h-4 w-4 mr-3 text-white/70" />
                    {t('common.profile')}
                  </Link>
                  <button
                    onClick={logout}
                    className="text-white group flex w-full items-center px-4 py-2 text-sm transition-colors hover:bg-white/10"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3 text-white/70" />
                    {t('common.logout')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main navigation bar - ALL MODULES with VERTICAL DROPDOWNS */}
        <nav className="px-4 md:px-6 pb-2 overflow-visible">
          <div className="flex items-center relative">
            {/* Left Arrow */}
            {showLeftArrow && (
              <button
                ref={leftArrowRef}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  scrollModules('left');
                }}
                className="absolute left-0 z-50 p-2 bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 rounded-full text-white shadow-lg transition-all"
                style={{ marginLeft: '1rem' }}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}

            {/* Modules Container */}
            <div 
              ref={modulesScrollRef}
              onScroll={checkScroll}
              className="flex items-center space-x-1 md:space-x-2 overflow-x-auto scrollbar-hide relative flex-1" 
              style={{ overflowY: 'visible', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {accessibleModules.map((module) => {
              const isActive = module.subsections.some(sub => location.pathname === sub.href || location.pathname.startsWith(sub.href));
              const IconComponent = module.icon;

              return (
                <div key={module.key} className="relative" ref={el => dropdownRefs.current[module.key] = el}>
                  <button
                    ref={el => moduleButtonRefs.current[module.key] = el}
                    onClick={() => {
                      // 🎯 NAVIGATE TO DASHBOARD + TOGGLE DROPDOWN (like sidebar)
                      // Navigate to the first subsection (dashboard) if it exists
                      if (module.subsections && module.subsections.length > 0) {
                        const firstSubsection = module.subsections[0];
                        navigate(firstSubsection.href);
                      }
                      // Toggle dropdown
                      setActiveDropdown(activeDropdown === module.key ? null : module.key);
                    }}
                    className={`flex items-center space-x-2 py-2 px-3 md:px-4 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'bg-white/20 text-white'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <IconComponent className="h-5 w-5" />
                    <span className="hidden md:inline">{getModuleLabel(module, t)}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === module.key ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Vertical Dropdown Menu */}
                  <AnimatePresence>
                    {activeDropdown === module.key && (
                      <motion.div
                        ref={dropdownContainerRef}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="fixed mt-2 w-64 bg-gradient-to-br from-slate-700 via-blue-750 to-slate-700 border border-blue-400/60 rounded-lg shadow-2xl z-[9999] overflow-hidden"
                        style={{
                          top: dropdownRefs.current[module.key]?.getBoundingClientRect().bottom + 'px',
                          left: dropdownRefs.current[module.key]?.getBoundingClientRect().left + 'px',
                          boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)'
                        }}
                      >
                        <div className="py-2 px-2">
                          {module.subsections.map((subsection, index) => {
                          const subIsActive = location.pathname === subsection.href || 
                            (subsection.href === '/pos/inventory' && location.pathname === '/pos/stock');
                          const SubIcon = subsection.icon;
                          const isFirst = index === 0;
                          const isLast = index === module.subsections.length - 1;
                          
                          return (
                            <Link
                              key={subsection.href}
                              to={subsection.href}
                              className={`group flex w-full items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                                subIsActive 
                                  ? 'bg-white/10 text-white' 
                                  : 'text-white/60 hover:bg-white/5 hover:text-white'
                              }`}
                            >
                              <SubIcon className={`h-4 w-4 mr-3 ${subIsActive ? 'text-blue-400' : 'text-white/70'}`} />
                              {getSubsectionLabel(subsection, t)}
                            </Link>
                          );
                        })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
            
            {/* Paramètres Généraux link */}
            <Link
              to="/settings"
              className={`flex items-center py-2 px-3 md:px-4 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                location.pathname.startsWith('/settings')
                  ? 'bg-white/20 text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <Cog6ToothIcon className="h-5 w-5 md:mr-2" aria-hidden="true" />
              <span className="hidden md:inline">{t('common.settings')}</span>
            </Link>
            </div>

            {/* Right Arrow */}
            {showRightArrow && (
              <button
                ref={rightArrowRef}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  scrollModules('right');
                }}
                className="absolute right-0 z-50 p-2 bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 rounded-full text-white shadow-lg transition-all"
                style={{ marginRight: '1rem' }}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default TopBar;

